import React, { useState, useEffect, useRef } from 'react';
import './CameroonMap.css';

const CameroonMap = () => {
  const [clickedSquares, setClickedSquares] = useState([]);
  const [winningSquares, setWinningSquares] = useState([]);
  const [attempts, setAttempts] = useState(5);
  const [message, setMessage] = useState('');
  const [gameOver, setGameOver] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [currentPrize, setCurrentPrize] = useState(null);
  
  // Liste des gains possibles
  const prizes = [
    "25 Go de connexion",
    "25 000 U de crédit",
    "25 000 cash",
    "1 iPhone"
  ];
  
  // Define the grid dimensions
  const rows = 10;
  const cols = 10;
  
  // Fonction pour obtenir l'index d'un carré à partir de sa position (row, col)
  const getSquareIndex = (row, col) => {
    return row * cols + col;
  };
  
  // Définir la forme du Cameroun (déplacé au niveau du composant pour être accessible partout)
  const cameroonShape = [
    // Partie Nord (Extrême Nord)
    { row: 0, col: 4 }, 
    // Partie Nord-Est (Nord)
    { row: 1, col: 3 }, { row: 1, col: 4 },
    // Partie Centrale (Adamaoua, Centre)
    { row: 2, col: 3 }, { row: 2, col: 4 }, { row: 2, col: 5 },
    { row: 3, col: 2 }, { row: 3, col: 3 }, { row: 3, col: 4 },
    // Partie Sud (Centre, Littoral, Est)
    { row: 4, col: 2 }, { row: 4, col: 3 }, { row: 4, col: 4 },
    { row: 5, col: 1 }, { row: 5, col: 2 }, { row: 5, col: 3 }, { row: 5, col: 4 },
    // Partie Sud-Ouest (Littoral, Sud, Sud-Ouest)
    { row: 6, col: 0 }, { row: 6, col: 1 }, { row: 6, col: 2 }, { row: 6, col: 3 }, { row: 6, col: 4 }, { row: 6, col: 5 },
    // Extrême Sud (Sud, Est)
    { row: 7, col: 0 }, { row: 7, col: 1 }, { row: 7, col: 2 }, { row: 7, col: 3 }, { row: 7, col: 4 }, { row: 7, col: 5 }, { row: 7, col: 6 },
    // Partie Est (Est)
    { row: 8, col: 1 }, { row: 8, col: 2 }, { row: 8, col: 3 }, { row: 8, col: 4 }, { row: 8, col: 5 }, { row: 8, col: 6 },
    // Ajout de carreaux supplémentaires pour mieux correspondre à l'image
    { row: 2, col: 6 }, { row: 3, col: 5 }, 
    { row: 5, col: 0 }, { row: 5, col: 5 }, 
    { row: 6, col: 6 },
    { row: 7, col: 7 },
     { row: 8, col: 7 },
     { row: 9, col: 2 }, { row: 9, col: 3 }, { row: 9, col: 4 }, { row: 9, col: 5 }, { row: 9, col: 6 }
    // Carreaux barrés supprimés: 5,5 (en haut), 9,0, 9,1 (en bas à gauche) et 10,2 (en bas)
  ];
  
  // Initialize the game by fetching map configuration from the API
  useEffect(() => {
    const initializeGame = async () => {
      try {
        setLoading(true);
        // Générer des positions gagnantes aléatoires
        const generateRandomWinningSquares = () => {
          // Nombre de trésors à placer
          const numberOfTreasures = 6;
          const winningPositions = [];
          
          // Créer une copie du tableau pour pouvoir supprimer des éléments sans modifier l'original
          const availablePositions = [...cameroonShape];
          
          // Sélectionner aléatoirement des positions dans la forme du Cameroun
          while (winningPositions.length < numberOfTreasures && availablePositions.length > 0) {
            // Sélectionner un index aléatoire
            const randomIndex = Math.floor(Math.random() * availablePositions.length);
            
            // Obtenir la position et l'ajouter aux positions gagnantes
            const position = availablePositions[randomIndex];
            const squareIndex = getSquareIndex(position.row, position.col);
            
            // Ajouter à notre tableau de positions gagnantes
            winningPositions.push(squareIndex);
            
            // Supprimer cette position des positions disponibles
            availablePositions.splice(randomIndex, 1);
          }
          
          return winningPositions;
        };
        
        // Générer et définir les positions gagnantes aléatoires
        setWinningSquares(generateRandomWinningSquares());
        setLoading(false);
      } catch (err) {
        setError('Erreur lors de l\'initialisation du jeu');
        setLoading(false);
        console.error('Error initializing game:', err);
      }
    };
    
    initializeGame();
  }, []);

  // Handle click on a square
  const handleSquareClick = (index) => {
    if (clickedSquares.includes(index) || gameOver) {
      return; // Already clicked or game over
    }
    
    // Mark this square as clicked
    setClickedSquares(prev => [...prev, index]);
    
    // Check if it's a winning square
    if (winningSquares.includes(index)) {
      // Sélectionner un gain aléatoire
      const randomPrize = prizes[Math.floor(Math.random() * prizes.length)];
      setCurrentPrize(randomPrize);
      
      // Mettre à jour le message avec le gain
      setMessage(`Félicitations ! Vous avez trouvé un trésor Orange et gagné ${randomPrize}!`);
      
      // Afficher l'animation de célébration et terminer le jeu
      setShowCelebration(true);
      setGameOver(true); // Marquer le jeu comme terminé pour empêcher d'autres clics
      
      // Ne plus masquer l'animation - elle restera affichée jusqu'à ce que le joueur relance la partie
      // setTimeout(() => {
      //   setShowCelebration(false);
      // }, 3000);
    } else {
      // Reduce attempts
      const newAttempts = attempts - 1;
      setAttempts(newAttempts);
      
      if (newAttempts <= 0) {
        setGameOver(true);
        setMessage('Game over ! Vous avez épuisé toutes vos tentatives.');
      } else {
        setMessage(`Essayez encore ! Il vous reste ${newAttempts} tentative${newAttempts > 1 ? 's' : ''}`);
      }
    }
  };
  
  // Reset the game
  const resetGame = () => {
    setClickedSquares([]);
    setAttempts(5);
    setMessage('');
    setGameOver(false);
    setShowCelebration(false); // Masquer l'animation de célébration
    
    // Générer de nouvelles positions gagnantes aléatoires
    const generateRandomWinningSquares = () => {
      const numberOfTreasures = 6;
      const winningPositions = [];
      
      // Copie du tableau des positions disponibles
      const availablePositions = [...cameroonShape];
      
      // Sélectionner aléatoirement des positions
      while (winningPositions.length < numberOfTreasures && availablePositions.length > 0) {
        const randomIndex = Math.floor(Math.random() * availablePositions.length);
        const position = availablePositions[randomIndex];
        const squareIndex = getSquareIndex(position.row, position.col);
        
        winningPositions.push(squareIndex);
        availablePositions.splice(randomIndex, 1);
      }
      
      return winningPositions;
    };
    
    // Appliquer les nouvelles positions gagnantes
    setWinningSquares(generateRandomWinningSquares());
  };
  
  // Render the grid
  const renderGrid = () => {
    const grid = [];
    
    // Calculate square size based on container size - adapt to 70vh container
    const squareSize = `calc(70vh / 12)`; // Diviser par 12 pour avoir 10 carreaux + marges
    const gap = `calc(${squareSize} / 10)`; // Espacement proportionnel à la taille des carreaux
    
    // Parcourir toutes les coordonnées et ajouter un carré pour chacune
    cameroonShape.forEach(({ row, col }) => {
      const index = getSquareIndex(row, col);
      const isClicked = clickedSquares.includes(index);
      const isWinning = winningSquares.includes(index) && isClicked;
      
      grid.push(
        <div 
          key={index}
          className={`square ${isClicked ? 'clicked' : ''}`}
          onClick={() => handleSquareClick(index)}
          style={{
            position: 'absolute',
            top: `calc(${row} * (${squareSize} + ${gap}))`,
            left: `calc(${col} * (${squareSize} + ${gap}))`,
            width: squareSize,
            height: squareSize
          }}
        >
          {isClicked && (
            <div className={`result-logo ${isWinning ? 'success' : 'failure'}`}>
              <img 
                src={isWinning ? "/success.svg" : "/retry.svg"}
                alt={isWinning ? "Succès" : "Essayez encore"}
                style={{ width: '100%', height: '100%' }}
              />
            </div>
          )}
        </div>
      );
    });
    
    return grid;
  };

  // Composant de célébration avec pétale, feux d'artifice et effets de loterie
  const Celebration = () => {
    const containerRef = useRef(null);
    
    useEffect(() => {
      if (!containerRef.current) return;
      
      const container = containerRef.current;
      container.innerHTML = '';
      
      // Ajouter des pétale et feux d'artifice
      const numElements = 50;
      for (let i = 0; i < numElements; i++) {
        const isFirework = i % 2 === 0;
        const element = document.createElement('div');
        
        element.className = isFirework ? 'firework' : 'petal';
        
        // Position aléatoire sur l'écran
        const left = Math.random() * 100; // En pourcentage
        const delay = Math.random() * 2; // Délai d'animation aléatoire
        
        element.style.left = `${left}%`;
        element.style.animationDelay = `${delay}s`;
        
        if (isFirework) {
          // Pour les feux d'artifice
          const tx = (Math.random() - 0.5) * 200;
          const ty = (Math.random() - 0.5) * 200;
          element.style.setProperty('--tx', `${tx}px`);
          element.style.setProperty('--ty', `${ty}px`);
          
          // Couleurs aléatoires pour les feux d'artifice
          const hue = Math.floor(Math.random() * 30) + 20; // Dans les tons orange
          element.style.background = `radial-gradient(circle, rgba(255,255,255,1) 0%, hsl(${hue}, 100%, 50%) 100%)`;
        } else {
          // Pour les pétale
          element.style.backgroundColor = `rgba(255, ${Math.floor(Math.random() * 100) + 100}, 0, ${Math.random() * 0.5 + 0.5})`;
          element.style.borderRadius = '40% 60% 30% 70% / 40% 50% 60% 50%';
          element.style.width = `${Math.random() * 10 + 5}px`;
          element.style.height = `${Math.random() * 10 + 5}px`;
          element.style.transform = `rotate(${Math.random() * 360}deg)`;
        }
        
        container.appendChild(element);
      }
      
      // Ajouter des confettis
      for (let i = 0; i < 30; i++) {
        const confetti = document.createElement('div');
        confetti.className = 'confetti';
        confetti.style.left = `${Math.random() * 100}%`;
        confetti.style.animationDelay = `${Math.random() * 3}s`;
        
        // Couleurs vives et variées pour les confettis
        const colors = ['#ff7900', '#ffcc00', '#ff00cc', '#00ccff', '#00ff00', '#ff0000', '#9900ff'];
        confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
        
        // Formes variées (carré, rectangle, cercle)
        const shapes = ['0%', '50%'];
        const borderRadius = shapes[Math.floor(Math.random() * shapes.length)];
        confetti.style.borderRadius = borderRadius;
        
        // Tailles variées
        confetti.style.width = `${Math.random() * 10 + 5}px`;
        confetti.style.height = `${Math.random() * 10 + 5}px`;
        
        container.appendChild(confetti);
      }
      
      // Ajouter des étoiles scintillantes
      for (let i = 0; i < 20; i++) {
        const star = document.createElement('div');
        star.className = 'star';
        
        // Position aléatoire sur l'écran
        star.style.left = `${Math.random() * 100}%`;
        star.style.top = `${Math.random() * 100}%`;
        
        // Taille aléatoire
        const size = Math.random() * 20 + 10;
        star.style.width = `${size}px`;
        star.style.height = `${size}px`;
        
        // Délai d'animation aléatoire pour qu'elles ne scintillent pas toutes en même temps
        star.style.animationDelay = `${Math.random() * 4}s`;
        
        // Couleur aléatoire entre blanc et jaune doré
        const colors = ['white', '#ffffcc', '#ffff99', '#ffffa8', '#ffed85'];
        star.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
        
        container.appendChild(star);
      }
      
      // Ajouter des symboles de loterie (emoji)
      const symbols = ['💰', '💎', '🏆', '💶', '🎁', '🎊', '🎯'];
      for (let i = 0; i < 15; i++) {
        const symbol = document.createElement('div');
        symbol.className = 'lottery-symbol';
        symbol.textContent = symbols[Math.floor(Math.random() * symbols.length)];
        
        // Position aléatoire
        symbol.style.left = `${Math.random() * 100}%`;
        symbol.style.top = `${Math.random() * 100}%`;
        
        // Délai d'animation aléatoire
        symbol.style.animationDelay = `${Math.random() * 5}s`;
        
        container.appendChild(symbol);
      }
      
      // Particules qui continuent à apparaître pour maintenir l'animation
      const particleInterval = setInterval(() => {
        // Limiter le nombre total de particules
        if (container.querySelectorAll('.petal, .firework, .confetti, .star, .lottery-symbol').length > 200) return;
        
        // Créer un élément aléatoire parmi les types disponibles
        const rand = Math.random();
        let element;
        
        if (rand < 0.2) {
          // Feu d'artifice (20% de chance)
          element = document.createElement('div');
          element.className = 'firework';
          const tx = (Math.random() - 0.5) * 200;
          const ty = (Math.random() - 0.5) * 200;
          element.style.setProperty('--tx', `${tx}px`);
          element.style.setProperty('--ty', `${ty}px`);
          const hue = Math.floor(Math.random() * 30) + 20;
          element.style.background = `radial-gradient(circle, rgba(255,255,255,1) 0%, hsl(${hue}, 100%, 50%) 100%)`;
        } else if (rand < 0.4) {
          // Pétale (20% de chance)
          element = document.createElement('div');
          element.className = 'petal';
          element.style.backgroundColor = `rgba(255, ${Math.floor(Math.random() * 100) + 100}, 0, ${Math.random() * 0.5 + 0.5})`;
          element.style.borderRadius = '40% 60% 30% 70% / 40% 50% 60% 50%';
          element.style.width = `${Math.random() * 10 + 5}px`;
          element.style.height = `${Math.random() * 10 + 5}px`;
          element.style.transform = `rotate(${Math.random() * 360}deg)`;
        } else if (rand < 0.7) {
          // Confetti (30% de chance)
          element = document.createElement('div');
          element.className = 'confetti';
          const colors = ['#ff7900', '#ffcc00', '#ff00cc', '#00ccff', '#00ff00', '#ff0000', '#9900ff'];
          element.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
          const shapes = ['0%', '50%'];
          element.style.borderRadius = shapes[Math.floor(Math.random() * shapes.length)];
          element.style.width = `${Math.random() * 10 + 5}px`;
          element.style.height = `${Math.random() * 10 + 5}px`;
        } else if (rand < 0.8) {
          // Étoile (10% de chance)
          element = document.createElement('div');
          element.className = 'star';
          const size = Math.random() * 20 + 10;
          element.style.width = `${size}px`;
          element.style.height = `${size}px`;
          element.style.top = `${Math.random() * 100}%`;
          const colors = ['white', '#ffffcc', '#ffff99', '#ffffa8', '#ffed85'];
          element.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
        } else {
          // Symbole de loterie (20% de chance)
          element = document.createElement('div');
          element.className = 'lottery-symbol';
          const symbols = ['💰', '💎', '🏆', '💶', '🎁', '🎊', '🎯'];
          element.textContent = symbols[Math.floor(Math.random() * symbols.length)];
          element.style.top = `${Math.random() * 100}%`;
        }
        
        // Position commune
        element.style.left = `${Math.random() * 100}%`;
        
        container.appendChild(element);
      }, 300);
      
      // Messages de félicitation
      const messageContainer = document.createElement('div');
      messageContainer.className = 'celebration-messages';
      
      // Message principal
      const congratsMessage = document.createElement('div');
      congratsMessage.className = 'message-celebration';
      congratsMessage.textContent = 'FÉLICITATIONS !';
      messageContainer.appendChild(congratsMessage);
      
      // Message du gain
      const prizeMessage = document.createElement('div');
      prizeMessage.className = 'prize-message';
      prizeMessage.textContent = `Vous avez gagné ${currentPrize} !`;
      messageContainer.appendChild(prizeMessage);
      
      container.appendChild(messageContainer);
      
      // Nettoyer l'intervalle lorsque le composant est démonté
      return () => clearInterval(particleInterval);
    }, [currentPrize]);
    
    return (
      <div className="celebration">
        <div className="celebration-container" ref={containerRef}></div>
      </div>
    );
  };

  if (loading) {
    return <div className="loading">Chargement de la carte...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="cameroon-map-container">
      <div className="attempts-counter">
        Tentatives restantes: {attempts} / 5
      </div>
      
      <div className="grid-container">
        <div className="grid">
          {renderGrid()}
        </div>
      </div>
      
      <div className="message">{message}</div>
      
      {gameOver && (
        <button onClick={resetGame} className="replay-button">
          Nouvelle partie
        </button>
      )}
      
      {showCelebration && <Celebration />}
    </div>
  );
};

export default CameroonMap;
